import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  MyLayers,
  TileLayer,
  VectorLayer,
  ImageLayer,
} from '../components/Mapping/components/Layers';
import MyMap from '../components/Mapping/components/Map/MyMap';
// import Popup from '../components/Mapping/components/Popup/Popup';
import { vector, xyz } from '../components/Mapping/components/Source';
import imgNorth from '../images/north.png';
import eviImg from '../images/evi.jpeg';
import ndviImg from '../images/ndvi.jpeg';
import ndmiImg from '../images/ndmi.jpeg';
import authHeader from '../services/auth-header';
import { get } from 'ol/proj';
import GeoJSON from 'ol/format/GeoJSON';
import { createEmpty, extend } from 'ol/extent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisVertical,
  faRectangleXmark,
} from '@fortawesome/free-solid-svg-icons';
import AppLoader from '../components/ui/AppLoader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function FarmsMobile() {
  const { rootApiUrl, isLogedIn } = useSelector((state) => state.global);
  const [searchParams] = useSearchParams();

  const [center, setCenter] = useState([78.9629, 20.5937]);
  const [imageURL, setImageURL] = useState('');
  const [farmDataOL, setFarmDataOL] = useState(null);
  const [measure, setMeasure] = useState(() => (searchParams.get('index') || 'ndvi').toLowerCase());
  const [showLegend, setShowLegend] = useState(false);
  const [extent, setExtent] = useState([]);
  const [legendURL, setLegendURL] = useState('');
  const [loading, setLoading] = useState(false);

  const getFarmData = async () => {
    const cropId = searchParams.get('farm') || searchParams.get('crop_id');
    if (!cropId) {
      toast.error('Farm ID is missing.');
      return null;
    }
    const requestOptions = {
      method: 'GET',
      headers: authHeader(),
      redirect: 'follow',
    };
    try {
      const response = await fetch(rootApiUrl + 'farmdata_mobile/' + cropId, requestOptions);
      if (response.ok) {
        const data = await response.json();
        if (data && data.features && data.features.length > 0) {
          const fc_ol = new GeoJSON().readFeature(data.features[0], {
            featureProjection: get('EPSG:4326'),
          });
          setExtent(fc_ol.getGeometry().getExtent());
          setCenter([
            data.features[0].properties.lon,
            data.features[0].properties.lat,
          ]);
          setFarmDataOL(fc_ol);
          return data.features[0].properties;
        }
      } else {
        toast.error('Failed to load farm details.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error loading farm data');
    }
    return null;
  };

  const getImagery = async (farmProps) => {
    const itemId = searchParams.get('item_id');
    const indexParam = (searchParams.get('index') || 'ndvi').toLowerCase();
    setMeasure(indexParam);

    const geometryId = farmProps?.observearth_id;
    if (!geometryId || !itemId) {
      return;
    }

    const domain = process.env.REACT_APP_OBSERVEARTH_DOMAIN || 'https://khetibuddy.observearth.com';
    const observearthApiKey =
      (farmProps?.observearth_api_key && farmProps.observearth_api_key.trim() !== '') ? farmProps.observearth_api_key :
      (localStorage.getItem("observearth_api_key") && localStorage.getItem("observearth_api_key").trim() !== '') ? localStorage.getItem("observearth_api_key") :
      (localStorage.getItem("observeearth_api_key") && localStorage.getItem("observeearth_api_key").trim() !== '') ? localStorage.getItem("observeearth_api_key") :
      (process.env.REACT_APP_OBSERVEARTH_API_KEY && process.env.REACT_APP_OBSERVEARTH_API_KEY.trim() !== '') ? process.env.REACT_APP_OBSERVEARTH_API_KEY :
      null;

    if (!observearthApiKey) {
      toast.error('Observeearth API key is not configured for this farm.');
      return;
    }

    const minVal = searchParams.get('min_val');
    const maxVal = searchParams.get('max_val');

    let url = `${domain}/api/s2/image/${geometryId}/?item_id=${itemId}&image_type=png&index=${indexParam}&pixelized=false`;
    if (minVal !== null && minVal !== undefined && minVal !== 'null') {
      url += `&min_val=${minVal}`;
    }
    if (maxVal !== null && maxVal !== undefined && maxVal !== 'null') {
      url += `&max_val=${maxVal}`;
    }

    console.log("Image Request URL:", url);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "x-api-key": observearthApiKey,
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Observeearth imagery API error: ${response.statusText}`);
      }

      const responseText = await response.text();
      const base64Data = responseText.trim();
      let imageUrl = '';
      if (base64Data.startsWith('{')) {
        try {
          const parsed = JSON.parse(base64Data);
          const val = parsed.url || parsed.image_url || parsed.image || parsed.data || parsed.base64;
          if (val) {
            imageUrl = val.startsWith('data:') ? val : `data:image/png;base64,${val}`;
          }
        } catch (e) {}
      }

      if (!imageUrl) {
        imageUrl = base64Data.startsWith('data:') ? base64Data : `data:image/png;base64,${base64Data}`;
      }

      setImageURL(imageUrl);
    } catch (err) {
      console.error(err);
      toast.error('Error fetching imagery from Observearth API');
    } finally {
      clearTimeout(timeoutId);
    }
  };

  useEffect(() => {
    const indexParam = (searchParams.get('index') || 'ndvi').toLowerCase();
    setMeasure(indexParam);

    async function init() {
      setLoading(true);
      const itemId = searchParams.get('item_id');
      if (!itemId) {
        toast.error('item_id query parameter is required.');
        setLoading(false);
        return;
      }
      
      const farmProps = await getFarmData();
      if (farmProps) {
        await getImagery(farmProps);
      }
      setLoading(false);
    }
    init();
  }, [searchParams]);

  return (
    <div className='fixed inset-0'>
      <AppLoader show={loading} />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='colored'
      />
      {searchParams.get('index') && (
        <span className='absolute z-10 p-3 bg-white rounded-lg shadow-lg bottom-4 left-4'>
          {!showLegend && (
            <span
              className='text-xs font-bold cursor-pointer text-ext-darker'
              onClick={() => {
                setShowLegend(!showLegend);
              }}
            >
              <FontAwesomeIcon icon={faEllipsisVertical} className='mr-2' /> Show
              Legend
            </span>
          )}
          {showLegend && (
            <>
              <FontAwesomeIcon
                icon={faRectangleXmark}
                className='absolute z-10 block text-2xl text-red-500 cursor-pointer -right-1 -top-1'
                onClick={() => {
                  setShowLegend(!showLegend);
                }}
              />
              <img
                src={
                  legendURL
                    ? rootApiUrl + legendURL
                    : measure === 'ndvi'
                    ? ndviImg
                    : measure === 'ndmi'
                    ? ndmiImg
                    : measure === 'evi'
                    ? eviImg
                    : ''
                }
                alt='legend'
                className='h-[35vh]'
              />
            </>
          )}
        </span>
      )}
      <MyMap center={center} zoom={5} trackLocation={false}>
        <img
          src={imgNorth}
          alt='north'
          className='absolute z-10 object-contain p-1 rounded-md w-9 h-9 bg-ext-green-light bg-opacity-60 right-2 top-2'
        />
        {/* <Popup type='wms' /> */}
        <MyLayers>
          <TileLayer
            source={xyz('http://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}')}
            zIndex={0}
          />
          {imageURL && (
            <ImageLayer
              zIndex={1}
              urlPath={
                imageURL.startsWith('data:') || imageURL.startsWith('http')
                  ? imageURL
                  : rootApiUrl + imageURL
              }
              extent={extent}
            />
          )}
          {farmDataOL && (
            <VectorLayer
              name='farmLayer'
              source={vector({
                features: [farmDataOL],
              })}
              zIndex={2}
            />
          )}
        </MyLayers>
      </MyMap>
    </div>
  );
}

export default FarmsMobile;
